import { useEffect, useState } from "react";
import { Scissors, X } from "lucide-react";

/**
 * HighlightCoachmark — animated discovery hint shown at the top of the
 * whitepaper reader. A 3-second looping demo simulates the highlight →
 * clip → pill flow so the user *sees* the gesture before doing it
 * themselves.
 *
 * Persistence: localStorage["oak_coachmark_highlight_v1"] = "dismissed"
 * Dismissal triggers: X button OR the user makes their first ≥4-char
 * selection on the page (organic discovery).
 */
const STORAGE_KEY = "oak_coachmark_highlight_v1";
const DEMO_LINE = "Quantum mechanics describes nature at atomic scales.";

export default function HighlightCoachmark() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) setShow(true);
    } catch {
      /* localStorage blocked → silently skip */
    }
  }, []);

  // Auto-dismiss the moment the user actually highlights something —
  // they've discovered the feature, no further nudge needed.
  useEffect(() => {
    if (!show) return;
    const onSelection = () => {
      const sel = window.getSelection?.();
      const text = sel?.toString?.() || "";
      if (text.trim().length >= 4) dismiss();
    };
    document.addEventListener("selectionchange", onSelection);
    return () => document.removeEventListener("selectionchange", onSelection);
  }, [show]);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "dismissed"); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="mt-4 mb-1 rounded-xl border border-[#00FF9D]/30 bg-[#00FF9D]/[0.06] px-4 py-3 animate-fadeup relative"
      data-testid="highlight-coachmark"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 rounded-full p-1 text-white/45 hover:text-white hover:bg-white/10 transition"
        aria-label="Dismiss tip"
        data-testid="highlight-coachmark-dismiss"
      >
        <X size={14} />
      </button>

      {/* Animated demo strip */}
      <div className="pr-6">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#00FF9D]/70 font-mono-oak mb-2">
          Try it · live demo
        </div>
        <div className="oak-coach-stage">
          <div className="oak-coach-line" data-testid="highlight-coachmark-line">
            {DEMO_LINE}
            <span className="oak-coach-sweep" aria-hidden="true" />
            <span className="oak-coach-caret" aria-hidden="true" />
          </div>
          <div className="oak-coach-pill" aria-hidden="true">
            <Scissors size={11} /> Clip to OAK-Pad
          </div>
        </div>
        <div className="text-[12px] text-white/70 mt-3 leading-snug font-display">
          <span className="text-[#9bffcc] font-semibold">Highlight any line</span>{" "}
          — it auto-clips into your OAK-Pad. Build a study guide as you read,
          then export to PDF or publish a public note.
        </div>
      </div>
    </div>
  );
}
